import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';

// Event names for ASC tracking
const ASC_EVENT_NAMES = {
  // Share Button & Dropdown Events
  OPEN_ASC_DROPDOWN: 'open_asc_dropdown',
  CLOSE_ASC_DROPDOWN: 'close_asc_dropdown',
  TOGGLE_ASC_AGENCY: 'toggle_asc_agency',
  SELECT_ALL_ASC_AGENCIES: 'select_all_asc_agencies',
  CLEAR_ASC_AGENCIES: 'clear_asc_agencies',
  SUBMIT_ASC_SHARE: 'submit_asc_share',
  RESPONSE_ASC_SHARE: 'response_asc_share',

  // Chat Interaction Events
  SHOW_ASC_CHATBOX: 'show_asc_chatbox',
  HIDE_ASC_CHATBOX: 'hide_asc_chatbox',

  // Event Sharing & Receiving Events
  SHARE_ASC_EVENT: 'share_asc_event',
  RECEIVE_ASC_EVENT: 'receive_asc_event',
  VIEW_ASC_SHARED_EVENT: 'view_asc_shared_event',
};

// =============================================================================
// A. Share Button & Dropdown Events
// =============================================================================

/**
 * Tracks when user opens the ASC share dropdown
 */
export const trackOpenASCDropdown = () => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.OPEN_ASC_DROPDOWN,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
  });
};

/**
 * Tracks when user closes the ASC share dropdown
 */
export const trackCloseASCDropdown = () => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.CLOSE_ASC_DROPDOWN,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
  });
};

/**
 * Tracks when user toggles an agency selection in the dropdown
 * @param {Object} params - The parameters for tracking agency toggle
 * @param {string} params.agencyId - The ID of the agency being toggled
 * @param {string} params.agencyName - The name of the agency being toggled
 * @param {boolean} params.isSelected - Whether the agency is being selected (true) or deselected (false)
 * @param {number} params.totalSelected - Total number of agencies currently selected
 */
export const trackToggleASCAgency = ({
  agencyId,
  agencyName,
  isSelected,
  totalSelected,
}) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.TOGGLE_ASC_AGENCY,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      agency_id: agencyId,
      agency_name: agencyName,
    },
    additionalKeys: {
      is_selected: isSelected,
      total_selected: totalSelected,
    },
  });
};

/**
 * Tracks when user clicks "Select All" agencies
 * @param {Object} params - The parameters for tracking select all action
 * @param {number} params.selectedAgencies - Number of agencies that were actually selected
 */
export const trackSelectAllASCAgencies = ({ selectedAgencies }) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.SELECT_ALL_ASC_AGENCIES,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    additionalKeys: {
      selected_agencies: selectedAgencies,
    },
  });
};

/**
 * Tracks when user clicks "Clear All" agencies
 */
export const trackClearASCAgencies = () => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.CLEAR_ASC_AGENCIES,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
  });
};

/**
 * Tracks when user submits the share form
 * @param {Object} params - The parameters for tracking share submission
 * @param {Array} params.agencyIds - Array of agency IDs being shared with
 * @param {boolean} params.hasMessage - Whether an optional message was included
 */
export const trackSubmitASCShare = ({ agencyIds, hasMessage }) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.SUBMIT_ASC_SHARE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      agency_ids: agencyIds,
    },
    additionalKeys: {
      has_message: hasMessage,
    },
  });
};

/**
 * Tracks the API response for share event
 * @param {Object} params - The parameters for tracking share response
 * @param {boolean} params.success - Whether the share was successful
 * @param {string} params.conversationId - The TalkJS conversation ID
 * @param {Array} params.agencyIds - Array of agency IDs that were shared with
 */
export const trackResponseASCShare = ({
  success,
  conversationId,
  agencyIds,
}) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.RESPONSE_ASC_SHARE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
      agency_ids: agencyIds,
    },
    additionalKeys: {
      success: success,
    },
  });
};

// =============================================================================
// B. Chat Interaction Events
// =============================================================================

/**
 * Tracks when user expands the ASC chat box
 * @param {Object} params - The parameters for tracking chat box expansion
 * @param {string} params.conversationId - The TalkJS conversation ID
 */
export const trackShowASCChatbox = ({ conversationId }) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.SHOW_ASC_CHATBOX,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
  });
};

/**
 * Tracks when user collapses the ASC chat box
 * @param {Object} params - The parameters for tracking chat box collapse
 * @param {string} params.conversationId - The TalkJS conversation ID
 */
export const trackHideASCChatbox = ({ conversationId }) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.HIDE_ASC_CHATBOX,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
  });
};

// =============================================================================
// C. Event Sharing & Receiving Events
// =============================================================================

/**
 * Tracks when an event is successfully shared
 * @param {Object} params - The parameters for tracking event sharing
 * @param {string} params.conversationId - The TalkJS conversation ID
 * @param {Array} params.agencyIds - Array of agency IDs the event was shared with
 * @param {string} params.eventType - Type of the event being shared
 */
export const trackShareASCEvent = ({
  conversationId,
  agencyIds,
  eventType,
}) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.SHARE_ASC_EVENT,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
      agency_ids: agencyIds,
      event_type: eventType,
    },
  });
};

/**
 * Tracks when an event is received from another agency
 * @param {Object} params - The parameters for tracking event receiving
 * @param {string} params.conversationId - The TalkJS conversation ID
 * @param {string} params.sharerAgency - Name of the agency that shared the event
 */
export const trackReceiveASCEvent = ({ conversationId, sharerAgency }) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.RECEIVE_ASC_EVENT,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
      sharer_agency: sharerAgency,
    },
  });
};

/**
 * Tracks when user clicks on a shared event (received toast)
 * @param {Object} params - The parameters for tracking shared event viewing
 * @param {string} params.conversationId - The TalkJS conversation ID
 * @param {string} params.sharerAgency - Name of the agency that shared the event
 */
export const trackViewASCSharedEvent = ({ conversationId, sharerAgency }) => {
  gspx.trackEvent({
    name: ASC_EVENT_NAMES.VIEW_ASC_SHARED_EVENT,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.AGENCY_SHARE_CHAT,
    capability: GPX_CAPABILITIES.AGENCY_SHARE_CHAT,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
      sharer_agency: sharerAgency,
    },
  });
};
